import axios from "axios";

const getExcelServiceToken = async () => {
    try {
        const res = await axios.get(import.meta.env.VITE_EXCELSERVICES_TOKEN_API_URL!)
        return res.data.token;
    } catch (err) {
        return err
    }
}

/** Height (m) to feet for Tree Calculator height range */
export function treeHeightMetersToFeet(m: number): number {
    return m * 3.28084;
}

/** Map height in feet to Tree Calculator height-range string */
export function getHeightRangeForTree(heightInFeet: number): string {
    if (heightInFeet <= 9) return "0 - 9";
    if (heightInFeet >= 10 && heightInFeet <= 30) return "10 - 30";
    if (heightInFeet >= 31 && heightInFeet <= 50) return "31 - 50";
    return "51+";
}

const getTotalCostFromExcelService = async (token: string, location: string, dbh: number, height: number, stump: string, haul: string, noOfObst: number) => {
    return await axios({
        method: 'post',
        url: import.meta.env.VITE_EXCELSERVICES_API_URL,
        data: {
            "ExcelFileName": "Tree Calculator",
            "SheetName": "Tree Calculator",
            "InputValues": [
                {
                    "Type": "String",
                    "CellNumber": "B3",
                    "CellValue": location
                },
                {
                    "Type": "String",
                    "CellNumber": "B4",
                    "CellValue": String(dbh)
                },
                {
                    "Type": "String",
                    "CellNumber": "B5",
                    "CellValue": String(height)
                },
                {
                    "Type": "String",
                    "CellNumber": "B6",
                    "CellValue": stump
                },
                {
                    "Type": "String",
                    "CellNumber": "B7",
                    "CellValue": haul
                },
                {
                    "Type": "String",
                    "CellNumber": "B8",
                    "CellValue": String(noOfObst)
                }
            ],
            "OutputCells": [
                "C3", "C4", "C5", "C6", "C7", "C8", "C10"
            ]

        },
        headers: {
            Authorization: `Bearer ${token}`
        }
    })
        .then((res) => {
            return res.data.OutputValues[6].CellValue
        })
        .catch((err) => {
            return err
        })
}

/** Tree cut cost with string inputs (e.g. height range "10 - 30", dbh "12 - 23") for Excel dropdowns */
const getTotalCostFromExcelServiceWithStrings = async (
    token: string,
    location: string,
    dbhStr: string,
    heightStr: string,
    stump: string,
    haul: string,
    noOfObst: number
): Promise<number | null> => {
    try {
        const res = await axios({
            method: "post",
            url: import.meta.env.VITE_EXCELSERVICES_API_URL,
            data: {
                ExcelFileName: "Tree Calculator",
                SheetName: "Tree Calculator",
                InputValues: [
                    { Type: "String", CellNumber: "B3", CellValue: location },
                    { Type: "String", CellNumber: "B4", CellValue: dbhStr },
                    { Type: "String", CellNumber: "B5", CellValue: heightStr },
                    { Type: "String", CellNumber: "B6", CellValue: stump },
                    { Type: "String", CellNumber: "B7", CellValue: haul },
                    { Type: "String", CellNumber: "B8", CellValue: String(noOfObst) },
                ],
                OutputCells: ["C3", "C4", "C5", "C6", "C7", "C8", "C10"],
            },
            headers: { Authorization: `Bearer ${token}` },
        });
        const val = res.data?.OutputValues?.[6]?.CellValue;
        return val != null ? Number(val) : null;
    } catch {
        return null;
    }
};

/** Default params for AI-driven tree cut cost: Back, Flush Cut, Yes, 0 obstructions */
export const TREE_CART_DEFAULT_LOCATION = "Back";
export const TREE_CART_DEFAULT_STUMP = "Flush Cut";
export const TREE_CART_DEFAULT_HAUL = "Yes";
export const TREE_CART_DEFAULT_NO_OBST = 0;
export const TREE_CART_DEFAULT_DBH = "12 - 23";

/** Inputs sent to the Tree Calculator Excel service (B3–B8). Height range is derived from the tree mesh. */
export type TreeCutExcelParams = {
    location: string;
    dbhStr: string;
    stump: string;
    haul: string;
    noOfObst: number;
};

export const TREE_CART_DEFAULT_PARAMS: TreeCutExcelParams = {
    location: TREE_CART_DEFAULT_LOCATION,
    dbhStr: TREE_CART_DEFAULT_DBH,
    stump: TREE_CART_DEFAULT_STUMP,
    haul: TREE_CART_DEFAULT_HAUL,
    noOfObst: TREE_CART_DEFAULT_NO_OBST,
};

/**
 * Presets for comparing cut cost vs the default used in AI tree-cutting scenarios.
 * Default row matches what runGhostScenariosAndSave / cards show.
 */
export const TREE_CUT_COST_PRESET_OPTIONS: Array<{ id: string; label: string; params: TreeCutExcelParams }> = [
    { id: "default", label: "Default (flush stump cut + haul wood)", params: { ...TREE_CART_DEFAULT_PARAMS } },
    {
        id: "grinded_haul_yes",
        label: "Grinded stump + haul wood",
        params: { ...TREE_CART_DEFAULT_PARAMS, stump: "Grinded", haul: "Yes" },
    },
    {
        id: "flush_no_haul",
        label: "Flush stump cut + no haul",
        params: { ...TREE_CART_DEFAULT_PARAMS, stump: "Flush Cut", haul: "No" },
    },
    {
        id: "grinded_no_haul",
        label: "Grinded stump + no haul",
        params: { ...TREE_CART_DEFAULT_PARAMS, stump: "Grinded", haul: "No" },
    },
];

/** Tree Calculator cell B3 — same strings as the Tree Cart form. */
export const TREE_CALCULATOR_LOCATION_OPTIONS = ["Front", "Back", "Left Side", "Right Side"] as const;
export type TreeCalculatorLocation = (typeof TREE_CALCULATOR_LOCATION_OPTIONS)[number];

/**
 * Vary only site location (B3); stump, haul, DBH, and obstructions match AI defaults.
 * Use with Compare cut options to see price sensitivity to location vs default Back.
 */
export const TREE_CUT_LOCATION_PRESET_OPTIONS: Array<{ id: string; label: string; params: TreeCutExcelParams }> =
    TREE_CALCULATOR_LOCATION_OPTIONS.map((loc) => ({
        id: `location_${loc.replace(/\s+/g, "_").toLowerCase()}`,
        label:
            loc === TREE_CART_DEFAULT_LOCATION
                ? `Location: ${loc} (default for scenarios)`
                : `Location: ${loc}`,
        params: { ...TREE_CART_DEFAULT_PARAMS, location: loc },
    }));

/** Row order in the unified compare table: Back (AI default location) first, then other site sides. */
export const TREE_CUT_COMPARE_LOCATION_ORDER: readonly TreeCalculatorLocation[] = [
    "Back",
    "Front",
    "Left Side",
    "Right Side",
];

/** One row per location × stump/haul preset (4×4 = 16) for a single compare table. */
export type TreeCutCompareUnifiedOption = {
    id: string;
    location: string;
    serviceLabel: string;
    params: TreeCutExcelParams;
    /** True when this row matches the card / AI scenario cut cost (Back + default stump/haul). */
    isCardDefault: boolean;
};

export const TREE_CUT_COMPARE_UNIFIED_OPTIONS: TreeCutCompareUnifiedOption[] = (() => {
    const out: TreeCutCompareUnifiedOption[] = [];
    for (const loc of TREE_CUT_COMPARE_LOCATION_ORDER) {
        for (const opt of TREE_CUT_COST_PRESET_OPTIONS) {
            out.push({
                id: `${opt.id}__${loc.replace(/\s+/g, "_").toLowerCase()}`,
                location: loc,
                serviceLabel: opt.label,
                params: { ...opt.params, location: loc },
                isCardDefault: loc === TREE_CART_DEFAULT_LOCATION && opt.id === "default",
            });
        }
    }
    return out;
})();

/** Tree cut cost for explicit Tree Calculator params. Tree height from userData (meters). */
export async function getTreeCutCostWithParams(
    token: string,
    tree: { userData: { height: number } },
    params: TreeCutExcelParams,
): Promise<number | null> {
    const heightFeet = treeHeightMetersToFeet(tree.userData.height);
    const heightStr = getHeightRangeForTree(heightFeet);
    return getTotalCostFromExcelServiceWithStrings(
        token,
        params.location,
        params.dbhStr,
        heightStr,
        params.stump,
        params.haul,
        params.noOfObst,
    );
}

/** Get tree cut cost using default values. Tree height from userData (assumed meters). */
export async function getTreeCutCostWithDefaults(
    token: string,
    tree: { userData: { treeId: string; height: number } },
): Promise<number | null> {
    return getTreeCutCostWithParams(token, tree, TREE_CART_DEFAULT_PARAMS);
}

/** Default compare-row id (Back + default stump/haul) — used when scenario has no override. */
export const TREE_CUT_CARD_DEFAULT_OPTION_ID = TREE_CUT_COMPARE_UNIFIED_OPTIONS.find((o) => o.isCardDefault)?.id ?? 'default__back';

type ScenarioCutOptionFields = {
    cutCost?: number | null;
    cutCostOptionId?: string | null;
    cutCostLocation?: string | null;
    cutCostServiceLabel?: string | null;
};

/** Short label for stump/haul + location shown on tree-cutting scenario cards. */
export function formatTreeCutOptionCardLabel(scenario: ScenarioCutOptionFields | null | undefined): string {
    if (scenario?.cutCostLocation && scenario?.cutCostServiceLabel) {
        return `${scenario.cutCostLocation} · ${scenario.cutCostServiceLabel}`;
    }
    return `${TREE_CART_DEFAULT_LOCATION} · Default (flush stump cut & haul wood)`;
}

export type TreeCutCostOptionApplyPayload = {
    scenarioIndex: number;
    optionId: string;
    location: string;
    serviceLabel: string;
    total: number | null;
};

/** Patch one scenario row with compare-modal cut $ / option fields. */
export function patchScenarioWithCutCostOption<T extends ScenarioCutOptionFields>(
    scenarios: T[],
    payload: TreeCutCostOptionApplyPayload,
): T[] {
    if (payload.scenarioIndex < 0 || payload.scenarioIndex >= scenarios.length) return scenarios;
    const next = [...scenarios];
    const prev = next[payload.scenarioIndex] ?? ({} as T);
    next[payload.scenarioIndex] = {
        ...prev,
        cutCost: payload.total,
        cutCostOptionId: payload.optionId,
        cutCostLocation: payload.location,
        cutCostServiceLabel: payload.serviceLabel,
    };
    return next;
}

/** Cut $ line for catalog cards: cost + selected compare option. */
export function formatTreeScenarioCutDetail(scenario: ScenarioCutOptionFields | null | undefined): string | null {
    const parts: string[] = [];
    if (scenario?.cutCost != null && Number.isFinite(Number(scenario.cutCost))) {
        parts.push(`$${Math.round(Number(scenario.cutCost))} cut`);
    }
    const optionLabel = formatTreeCutOptionCardLabel(scenario);
    if (optionLabel) parts.push(optionLabel);
    return parts.length > 0 ? parts.join(' · ') : null;
}

const treeCartService = {
    getExcelServiceToken,
    getTotalCostFromExcelService,
    getTotalCostFromExcelServiceWithStrings,
    getTreeCutCostWithDefaults,
}

export default treeCartService;
